import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Layout } from "./components/Layout";
import { DataProvider } from "./lib/data";
import Category from "./pages/Category";
import { Guide, GuidesIndex } from "./pages/Guides";
import Home from "./pages/Home";
import { IngredientPage, IngredientsIndex } from "./pages/Ingredient";
import NotFound from "./pages/NotFound";
import Plan from "./pages/Plan";
import Fridge from "./pages/Fridge";
import FridgeShare from "./pages/FridgeShare";
import Recipe from "./pages/Recipe";
import RecipesIndex from "./pages/RecipesIndex";
import { Om, Privatliv } from "./pages/Static";
import { HvorMegetPrPerson, Madbudget, Portionsberegner } from "./pages/tools/Calculators";
import { AirfryerOmregner, Maengdeomregner, Ovntemperatur } from "./pages/tools/Converters";
import { ToolsIndex } from "./pages/tools/ToolPage";

const AdminApp = lazy(() => import("./admin/AdminApp"));

const TOOL_PAGES = {
  portionsberegner: Portionsberegner, maengdeomregner: Maengdeomregner, "airfryer-omregner": AirfryerOmregner,
  ovntemperatur: Ovntemperatur, "hvor-meget-pr-person": HvorMegetPrPerson, madbudget: Madbudget,
};

// Internal <a href="/..."> links navigate client-side (keeps source markup as plain anchors).
function Navigation() {
  const nav = useNavigate();
  const { pathname, hash } = useLocation();
  useEffect(() => {
    const h = e => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest && e.target.closest("a");
      const href = a && a.getAttribute("href");
      if (!href || !href.startsWith("/") || href.startsWith("//") || a.target || href.startsWith("/api/")) return;
      e.preventDefault();
      nav(href);
    };
    document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, [nav]);
  useEffect(() => { if (!hash) window.scrollTo(0, 0); }, [pathname, hash]);
  return null;
}

function TrailingSlash({ children }) {
  const { pathname, search, hash } = useLocation();
  if (!pathname.endsWith("/") && !pathname.split("/").pop().includes(".")) return <Navigate to={pathname + "/" + search + hash} replace />;
  return children;
}

export default function App() {
  return (
    <DataProvider>
      <BrowserRouter>
        <Navigation />
        <TrailingSlash>
          <Routes>
            <Route path="/admin/*" element={<Suspense fallback={null}><AdminApp /></Suspense>} />
            <Route path="*" element={<Layout><PublicRoutes /></Layout>} />
          </Routes>
        </TrailingSlash>
      </BrowserRouter>
    </DataProvider>
  );
}

function PublicRoutes() {
  return (
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/opskrifter/" element={<RecipesIndex />} />
              <Route path="/opskrifter/:slug/" element={<Recipe />} />
              <Route path="/kategori/:key/" element={<Category />} />
              <Route path="/opskrifter-med/" element={<IngredientsIndex />} />
              <Route path="/opskrifter-med/:slug/" element={<IngredientPage />} />
              <Route path="/guides/" element={<GuidesIndex />} />
              <Route path="/guides/:slug/" element={<Guide />} />
              <Route path="/vaerktoejer/" element={<ToolsIndex />} />
              {Object.entries(TOOL_PAGES).map(([slug, C]) => <Route key={slug} path={`/vaerktoejer/${slug}/`} element={<C />} />)}
              <Route path="/madplan/" element={<Plan />} />
              <Route path="/koeleskabet/" element={<Fridge />} />
              <Route path="/koeleskabet/del/:id/" element={<FridgeShare />} />
              <Route path="/om/" element={<Om />} />
              <Route path="/privatliv/" element={<Privatliv />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
  );
}
